import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Copy, Download, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

const COPY_BLOCKS = (link, code) => [
  {
    label: 'X / Twitter',
    text: `I plan a whole week of social posts in minutes with PostToSocial ✍️ AI captions, hashtags, images & video scripts — plus scheduling.\n\nTry it 👉 ${link}\n(use code ${code})`,
  },
  {
    label: 'LinkedIn',
    text: `If you're still writing every social post from scratch, you're wasting hours.\n\nI use PostToSocial to generate a week of on-brand posts — captions, hashtags, image prompts and short-form video scripts — then schedule and auto-publish them.\n\nIf you want to try it, here's my link: ${link} (code: ${code})`,
  },
  {
    label: 'Email',
    text: `Subject: The tool I use to batch a week of social content\n\nHey,\n\nI've been using PostToSocial to generate and schedule a full week of social posts in minutes — captions, hashtags, images and even video scripts, all on-brand.\n\nIf you want to check it out, use my link: ${link} (referral code ${code}).\n\nCheers!`,
  },
  {
    label: 'Instagram / DM',
    text: `Okay this one's a game-changer 🔥 PostToSocial writes a week of posts (captions + hashtags + image & video ideas) in minutes. Grab it here: ${link} — code ${code} 💜`,
  },
];

// Built-in banners (always available). Custom admin banners are appended.
const BUILTIN_BANNERS = [
  {
    id: 'builtin-landscape', name: 'Default post', layout: 'landscape',
    image: '/affiliate/banner-landscape.png', headline: 'A week of social content\nin minutes — with AI',
    subheadline: '', text_color: '#ffffff', builtin: true,
  },
  {
    id: 'builtin-portrait', name: 'Default story', layout: 'portrait',
    image: '/affiliate/banner-portrait.png', headline: 'A week of social\ncontent — with AI',
    subheadline: '', text_color: '#ffffff', builtin: true,
  },
];

function fitFont(ctx, text, maxWidth, startPx, weight) {
  let px = startPx;
  ctx.font = `${weight} ${px}px Inter, Arial, sans-serif`;
  while (ctx.measureText(text).width > maxWidth && px > 10) {
    px -= 1;
    ctx.font = `${weight} ${px}px Inter, Arial, sans-serif`;
  }
  return px;
}

// Draws a banner: background image + brand tag + (admin-configured) headline &
// subheadline + the affiliate's code pill and link (always overlaid).
function drawBanner(canvas, img, cfg) {
  const { code, link, headline, subheadline, textColor, layout } = cfg;
  const W = img.naturalWidth; const H = img.naturalHeight;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, W, H);
  const pad = Math.round(W * 0.06);
  const maxW = W - pad * 2;
  const portrait = layout === 'portrait';
  ctx.textAlign = portrait ? 'center' : 'left';
  const cx = portrait ? W / 2 : pad;
  const color = textColor || '#ffffff';

  // Brand tag
  ctx.fillStyle = '#c7d2fe';
  ctx.font = `700 ${Math.round(H * 0.036)}px Inter, Arial, sans-serif`;
  ctx.textBaseline = 'top';
  ctx.fillText('PostToSocial', cx, pad);

  // Headline (1-3 lines, auto-fit to width)
  const hLines = (headline || '').split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 3);
  let hSize = Math.round(H * (portrait ? 0.055 : 0.075));
  hLines.forEach((ln) => { hSize = Math.min(hSize, fitFont(ctx, ln, maxW, hSize, '800')); });

  ctx.fillStyle = color;
  ctx.textBaseline = 'alphabetic';
  ctx.font = `800 ${hSize}px Inter, Arial, sans-serif`;
  ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = 12;
  let y = H * 0.46;
  hLines.forEach((ln, i) => { ctx.fillText(ln, cx, y + i * hSize * 1.15); });
  let cursor = y + Math.max(0, hLines.length - 1) * hSize * 1.15;
  ctx.shadowBlur = 0;

  // Subheadline (optional)
  if (subheadline && subheadline.trim()) {
    const sSize = fitFont(ctx, subheadline, maxW, Math.round(H * 0.034), '600');
    ctx.font = `600 ${sSize}px Inter, Arial, sans-serif`;
    ctx.fillStyle = color; ctx.globalAlpha = 0.92;
    cursor += Math.round(H * 0.045) + sSize;
    ctx.fillText(subheadline, cx, cursor);
    ctx.globalAlpha = 1;
  }

  // Code pill (auto-fit) — always overlaid
  const pillY = cursor + Math.round(H * 0.045);
  const pillText = `Use code  ${code}`;
  const pillFont = fitFont(ctx, pillText, maxW - Math.round(H * 0.05), Math.round(H * 0.036), '700');
  ctx.font = `700 ${pillFont}px Inter, Arial, sans-serif`;
  ctx.textBaseline = 'alphabetic';
  const tw = ctx.measureText(pillText).width;
  const pw = tw + Math.round(H * 0.05); const ph = Math.round(pillFont * 2);
  const ppx = portrait ? (W - pw) / 2 : pad;
  ctx.fillStyle = '#4f46e5';
  const r = ph / 2;
  ctx.beginPath();
  ctx.moveTo(ppx + r, pillY); ctx.arcTo(ppx + pw, pillY, ppx + pw, pillY + ph, r);
  ctx.arcTo(ppx + pw, pillY + ph, ppx, pillY + ph, r); ctx.arcTo(ppx, pillY + ph, ppx, pillY, r);
  ctx.arcTo(ppx, pillY, ppx + pw, pillY, r); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#ffffff'; ctx.textBaseline = 'middle';
  ctx.fillText(pillText, ppx + Math.round(H * 0.025), pillY + ph / 2);

  // Link / CTA (auto-fit)
  ctx.fillStyle = '#e0e7ff';
  ctx.textBaseline = 'alphabetic';
  const linkText = `👉  ${link.replace(/^https?:\/\//, '')}`;
  const linkFont = fitFont(ctx, linkText, maxW, Math.round(H * 0.030), '600');
  ctx.font = `600 ${linkFont}px Inter, Arial, sans-serif`;
  ctx.fillText(linkText, cx, pillY + ph + Math.round(H * 0.05));
}

const BannerCard = ({ banner, code, link }) => {
  const canvasRef = useRef(null);
  const src = banner.builtin ? banner.image : banner.image_url;
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (canvasRef.current) {
        drawBanner(canvasRef.current, img, {
          code, link, headline: banner.headline, subheadline: banner.subheadline,
          textColor: banner.text_color, layout: banner.layout,
        });
      }
    };
    img.onerror = () => {};
    img.src = src;
  }, [src, code, link, banner.headline, banner.subheadline, banner.text_color, banner.layout]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('Could not render banner'); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `posttosocial-referral-${banner.layout}.png`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Banner downloaded');
    }, 'image/png');
  };

  return (
    <div className="space-y-2" data-testid={`banner-item-${banner.id}`}>
      <canvas ref={canvasRef} className={`w-full rounded-lg border border-zinc-200 ${banner.layout === 'portrait' ? 'max-w-[220px] mx-auto' : ''}`} data-testid={`banner-canvas-${banner.id}`} />
      <p className="text-[11px] text-zinc-500 text-center truncate">{banner.name}</p>
      <Button size="sm" variant="outline" className="w-full" onClick={download} data-testid={`download-banner-${banner.id}`}>
        <Download className="w-3.5 h-3.5 mr-1.5" /> Download
      </Button>
    </div>
  );
};

export const ReferralShareKit = ({ link, code }) => {
  const { api } = useAuth();
  const [custom, setCustom] = useState([]);
  const blocks = COPY_BLOCKS(link, code);
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied — paste it anywhere'); };

  useEffect(() => {
    api.get('/affiliate/banners').then((r) => setCustom(r.data?.banners || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const banners = [...BUILTIN_BANNERS, ...custom];

  return (
    <Card data-testid="referral-share-kit">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Megaphone className="w-4 h-4 text-indigo-600" /> Promote in one click</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <p className="text-xs font-medium text-zinc-600 mb-2">Prewritten posts (link &amp; code included)</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {blocks.map((b) => (
              <div key={b.label} className="rounded-lg border border-zinc-200 p-3 flex flex-col">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-semibold text-indigo-600">{b.label}</span>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => copy(b.text)} data-testid={`copy-${b.label.split(' ')[0].toLowerCase()}`}>
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                </div>
                <p className="text-[11px] text-zinc-500 whitespace-pre-wrap line-clamp-5">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-zinc-600 mb-2">Branded banners (your code baked in)</p>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="referral-banner-gallery">
            {banners.map((b) => <BannerCard key={b.id} banner={b} code={code} link={link} />)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralShareKit;
